import { Component } from '@theme/component';

/**
 * Scrolls the topbar text, but only when it does not fit.
 *
 * The markup wraps normally by default, so if this script never runs the text
 * is still fully readable — it just wraps instead of scrolling.
 */
class AnnouncementTopbarComponent extends Component {
  #resizeObserver = null;
  #clone = null;

  connectedCallback() {
    super.connectedCallback?.();
    this.#measure();

    // Observe the bar, not the host: a custom element defaults to display:inline
    // and reports nothing useful.
    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    if (this.bar) this.#resizeObserver.observe(this.bar);

    document.fonts?.ready.then(() => this.#measure());
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#resizeObserver?.disconnect();
  }

  get bar() {
    return this.querySelector('.announcement-topbar');
  }

  get track() {
    return this.querySelector('.announcement-topbar__track');
  }

  get text() {
    return this.querySelector('.announcement-topbar__text');
  }

  #measure() {
    const bar = this.bar;
    const track = this.track;
    const text = this.text;
    if (!bar || !track || !text || this.dataset.scrollWhenTooLong !== 'true') return;

    // Measure against a single unwrapped line, without the duplicate in the way.
    this.#teardown();

    const available = bar.clientWidth - this.#horizontalPadding(bar);

    // The text wraps by default, and a wrapped element reports its wrapped
    // width, so force one line for the measurement itself.
    text.style.whiteSpace = 'nowrap';
    const needed = text.scrollWidth;
    text.style.whiteSpace = '';

    if (needed <= available) return;

    const gap = Number(this.dataset.marqueeGap) || 48;
    const speed = Number(this.dataset.marqueeSpeed) || 40; // px per second
    const distance = needed + gap;

    this.#clone = text.cloneNode(true);
    this.#clone.setAttribute('aria-hidden', 'true');
    track.append(this.#clone);

    track.style.setProperty('--announcement-topbar-marquee-distance', `${distance}px`);
    track.style.setProperty('--announcement-topbar-marquee-duration', `${distance / speed}s`);
    bar.classList.add('announcement-topbar--marquee');
  }

  #teardown() {
    this.#clone?.remove();
    this.#clone = null;
    this.bar?.classList.remove('announcement-topbar--marquee');
  }

  #horizontalPadding(element) {
    const style = getComputedStyle(element);
    return parseFloat(style.paddingInlineStart) + parseFloat(style.paddingInlineEnd);
  }
}

if (!customElements.get('announcement-topbar-component')) {
  customElements.define('announcement-topbar-component', AnnouncementTopbarComponent);
}
